import { Article } from '../../components/Layout'
import { H1, H2, H3, P, Lead, UL, LI, Callout, Code, CodeBlock, PropTable, Steps, Step, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is Coffee API mode?', level: 2 as const },
  { id: 'cup-list', title: 'cup_list', level: 2 as const },
  { id: 'single', title: 'single', level: 2 as const },
  { id: 'children', title: 'children', level: 2 as const },
  { id: 'response', title: 'Response shape', level: 2 as const },
  { id: 'vs-drinker', title: 'Coffee API vs Drinker', level: 2 as const },
]

export function CoffeeApi() {
  return (
    <Article toc={toc}>
      <H1>Coffee API Mode</H1>
      <Lead>Coffee API mode lets a View fetch records directly from a Cup — no Drinker script required. It is faster, simpler, and supports filtering, ordering, and pagination out of the box.</Lead>

      <H2 id="what">What is Coffee API mode?</H2>
      <P>When you set a View's data source to <strong>Coffee API</strong>, Brainer calls <Code>findPaginatedResolved()</Code> or one of its siblings internally and returns the result directly to the view. There is no JavaScript to write.</P>
      <P>Three modes are available:</P>
      <UL>
        <LI><Code>cup_list</Code> — All records in a Cup, paginated and filterable</LI>
        <LI><Code>single</Code> — One specific record by ID</LI>
        <LI><Code>children</Code> — Child records of a given parent record</LI>
      </UL>

      <H2 id="cup-list">cup_list</H2>
      <P>Returns a paginated list of all Coffees in the Cup identified by the linked Input Parameter (the param's value must be a Cup UUID).</P>
      <Steps>
        <Step n={1} title="Set mode to Cup list">In the view wizard, open the <strong>API</strong> tab and select <strong>Coffee list — by cup ID</strong>.</Step>
        <Step n={2} title="Choose the input param">Select the parameter that holds the Cup ID (you may need to add it in the Parameters tab first).</Step>
        <Step n={3} title="Discover columns">Enter a sample Cup ID and click <strong>Discover</strong>. Rabbit fetches one record and shows all available fields as column options.</Step>
        <Step n={4} title="Enable columns">Check the columns you want to display. Uncheck the rest.</Step>
        <Step n={5} title="Configure filters and ordering (optional)">Add filter conditions and sort order in the panels below the column list. See <a href="/docs/plates/filters" className="text-blue-600 underline underline-offset-2">Filters &amp; Ordering</a>.</Step>
      </Steps>
      <Screenshot alt="View wizard API tab showing cup_list mode with an input param selected and columns discovered" caption="The Discover button fetches a sample record to auto-detect available columns." />

      <H2 id="single">single</H2>
      <P>Returns one Coffee by ID. The linked Input Parameter must hold a Coffee UUID. Useful for detail views — e.g. show the full details of the customer selected on another plate.</P>

      <H2 id="children">children</H2>
      <P>Returns all child Coffees related to a given parent Coffee. The linked Input Parameter must hold the parent Coffee UUID. You also specify the <Code>childCupTitle</Code> to narrow to one child type.</P>
      <Callout type="info">The <Code>children</Code> mode uses the Flavor (relation link) table internally. It returns all children regardless of which Cup Relation label links them.</Callout>

      <H2 id="response">Response shape</H2>
      <P>All three modes return the same top-level structure, keyed by the Cup's title:</P>
      <CodeBlock language="json" title="cup_list response">
{`{
  "Product": [
    {
      "uuid": "abc-123",
      "created_at": "2026-05-17T10:00:00Z",
      "updated_at": "2026-05-17T10:00:00Z",
      "Name": "Blue Widget",
      "SKU": "BLU-001",
      "Price": "19.99",
      "Client": { "id": "xyz-789", "identifier": "Acme Corp" },
      "Category": { "id": null, "identifier": null }
    }
  ],
  "page": 1,
  "limit": 50,
  "total": 142,
  "totalPages": 3
}`}
      </CodeBlock>
      <P>Each record includes:</P>
      <UL>
        <LI><Code>uuid</Code> — The record's unique ID</LI>
        <LI><Code>created_at</Code> / <Code>updated_at</Code> — Timestamps</LI>
        <LI>One key per Trait, with the bean value as the value (or <Code>null</Code> if empty)</LI>
        <LI>One key per Cup Relation label, with <Code>{`{ id, identifier }`}</Code> for the parent record (or <Code>{`{ id: null, identifier: null }`}</Code> if no parent)</LI>
      </UL>
      <Callout type="tip">The consistent null-parent shape (all relation labels always present) makes column discovery reliable — you can always reference <Code>Client.id</Code> or <Code>Client.identifier</Code> in column config even if some records have no client set.</Callout>

      <H2 id="vs-drinker">Coffee API vs Drinker</H2>
      <div className="overflow-x-auto rounded-lg border border-slate-200 mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider"> </th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Coffee API</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Drinker</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-[13px] text-slate-600">
            {[
              ['Setup', 'No code — wizard only', 'Write JavaScript'],
              ['Performance', 'Highly optimised, indexed SQL', 'Custom query, varies'],
              ['Filtering', 'Built-in condition builder', 'Write conditions in code'],
              ['Ordering', 'Built-in sort builder', 'Write order in code'],
              ['Pagination', 'Built-in with totalPages', 'Implement manually'],
              ['Custom aggregations', '✗ Not supported', '✓ Full flexibility'],
              ['Cross-entity joins', 'Parent relations only', 'Any join'],
              ['Best for', 'Standard CRUD views', 'Complex computed data'],
            ].map(([label, api, drinker]) => (
              <tr key={label} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-700">{label}</td>
                <td className="px-4 py-2">{api}</td>
                <td className="px-4 py-2">{drinker}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Article>
  )
}
